import {
  createFileRoute,
  Link,
} from "@tanstack/react-router";
import {
  ArrowLeft,
  Heart,
  Bookmark,
  MessageCircle,
  MoreHorizontal,
  Share2,
  Link2,
  type LucideIcon,
} from "lucide-react";
import { useLingui } from "@lingui/react/macro";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { helpApi } from "@/lib/api/services";
import {
  ArticleThumb,
  ArticleComments,
  PopularTopicsCard,
  CategoryPill,
  initials,
  formatArticleDate,
} from "@/components/help-center";

export const Route = createFileRoute(
  "/(protected)/_layout/help-center/$articleId",
)({
  loader: async ({ params }) => ({
    article: await helpApi.getArticle(params.articleId),
    comments: await helpApi.getComments(params.articleId),
    popularTopics: await helpApi.getPopularTopics(),
  }),
  component: ArticleDetailPage,
});

function BackLink({ label }: { label: string }) {
  return (
    <Link
      to="/help-center"
      search={{ category: "all", sort: "newest", view: "list" }}
      className="flex w-fit items-center gap-2 px-3 text-base font-medium text-(--gray-600)"
    >
      <ArrowLeft className="size-5" />
      {label}
    </Link>
  );
}

function StatPill({ icon: Icon, count }: { icon: LucideIcon; count: number }) {
  return (
    <span className="flex items-center gap-1 rounded-md border border-(--gray-200) bg-(--gray-100) px-2.5 py-1 text-sm text-(--gray-600)">
      <Icon className="size-5" />
      {count}
    </span>
  );
}

function ShareButton({ icon: Icon, label }: { icon: LucideIcon; label: string }) {
  return (
    <Button variant="brand-outline" size="icon" className="size-12" aria-label={label}>
      <Icon className="size-6" />
    </Button>
  );
}

function ArticleDetailPage() {
  const { t } = useLingui();
  const { article, comments, popularTopics } = Route.useLoaderData();

  if (!article) {
    return (
      <div className="flex flex-col gap-5 py-5 px-3">
        <BackLink label={t`Back to Help Center`} />
        <p className="px-3 py-8 text-(--gray-500)">{t`Article not found.`}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5 py-5 px-3">
      <BackLink label={t`Back to Help Center`} />

      <div className="flex items-start gap-3">
        <article className="flex min-w-0 flex-1 flex-col gap-3 px-2">
          <ArticleThumb category={article.category} imageUrl={article.imageUrl} className="h-75 w-full" />

          <div className="flex items-start justify-between gap-3">
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <StatPill icon={Heart} count={article.likeCount} />
                <StatPill icon={Bookmark} count={article.bookmarkCount} />
                <StatPill icon={MessageCircle} count={article.commentCount} />
              </div>
              <h1 className="text-4xl font-semibold text-(--color-text-primary)">
                {article.title}
              </h1>
            </div>

            <div className="flex shrink-0 items-center gap-2">
              <ShareButton icon={MoreHorizontal} label={t`More options`} />
              <ShareButton icon={Share2} label={t`Share`} />
              <ShareButton icon={Link2} label={t`Copy link`} />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <CategoryPill category={article.category} className="px-2.5 py-1 text-xs" />
            <div className="flex items-center gap-2">
              <Avatar className="size-6 rounded-md border border-(--gray-200)">
                <AvatarImage src={article.author.avatarUrl} alt="" />
                <AvatarFallback className="rounded-md text-[0.625rem]">
                  {initials(article.author.name)}
                </AvatarFallback>
              </Avatar>
              <span className="text-sm font-medium text-(--color-text-primary)">
                {article.author.name}
              </span>
            </div>
            <span className="text-sm text-(--gray-500)">•</span>
            <span className="text-sm text-(--color-text-primary)">
              {formatArticleDate(article.publishedAt)}
            </span>
          </div>

          <div className="flex flex-col gap-4 py-2">
            {article.body.map((paragraph, i) => (
              <p key={i} className="text-base leading-relaxed text-(--color-text-primary)">
                {paragraph}
              </p>
            ))}
          </div>

          <ArticleComments comments={comments} />
        </article>

        <aside className="hidden w-100 shrink-0 lg:block">
          <PopularTopicsCard topics={popularTopics} />
        </aside>
      </div>
    </div>
  );
}
